import React, { useState, useEffect, useRef } from 'react'
import { useWindowDimensions } from 'react-native'
import { useNavigation } from '@react-navigation/native'

import { graphql } from 'src/services/graphql'
import { Gong } from 'src/state/models/audio'
import { fonts } from 'src/theme'

import { buildComponent } from 'src/components/factory'
import { useStore, useStoreState } from 'src/hooks/state'
import { useAppearance } from 'src/hooks/use-appearance'
import { useGongPreview } from 'src/hooks/use-gong-preview'
import { getGongDownloadUrl } from 'src/helpers/gong-url'

import * as UI from 'native-base'
import * as Icons from 'src/components/icons'
import * as Semantics from 'src/components/semantics'
import Player from 'react-native-video'

// Composant Item pour les options de gong
const GongItem = buildComponent<{
  gong: Gong | null
  isSelected: boolean
  onSelect: () => void
  onPlay?: () => void
  isPlaying?: boolean
  isLoading?: boolean
  locale: string
}>()
  .withLocale('screens.connected.account-gong.screen')
  .withRender(({ props, locale }) => {
    const isNoGong = !props.gong

    let title: string

    if (isNoGong) {
      title = locale('options.noGong.title')
    } else {
      const gong = props.gong!
      title =
        (gong.short as any)[props.locale] || (gong.short as any).fr || gong.slug
    }

    const { width: windowWidth } = useWindowDimensions()
    const textWidth = (windowWidth || 600) * 0.48

    return (
      <UI.Box paddingX={4} paddingY={1}>
        <Semantics.Button
          onPress={props.onSelect}
          colorScheme={props.isSelected ? 'white' : 'primary'}
          margin={0}
          borderRadius={16}
          pl={4}
          pr={6}
          py={4}
          width="100%"
          _stack={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <UI.Row
            alignItems="center"
            space={3}
            width="100%"
            flexShrink={1}
            position="relative"
            paddingRight={props.onPlay ? 20 : 0}
          >
            <UI.Box width={24}>
              <Icons.FontAwesomeIcon
                size="lg"
                icon={isNoGong ? Icons.Solid.faVolumeXmark : Icons.Solid.faBell}
                color={props.isSelected ? 'black' : 'white'}
              />
            </UI.Box>
            <UI.Column overflow="hidden" width={textWidth} pl={2}>
              <UI.Text
                style={fonts.bold}
                color={props.isSelected ? 'black' : 'white'}
                fontSize="md"
              >
                {title}
              </UI.Text>
            </UI.Column>
            {props.onPlay && (
              <UI.Box
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
              >
                <UI.Pressable
                  onPress={props.onPlay}
                  disabled={props.isLoading}
                  width={12}
                  height={12}
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={
                    props.isSelected
                      ? 'rgba(0,0,0,0.08)'
                      : 'rgba(255,255,255,0.18)'
                  }
                  _pressed={{
                    backgroundColor: props.isSelected
                      ? 'rgba(0,0,0,0.18)'
                      : 'rgba(255,255,255,0.32)',
                  }}
                  hitSlop={12}
                >
                  {props.isLoading ? (
                    <UI.Spinner
                      size="sm"
                      color={props.isSelected ? 'black' : 'white'}
                    />
                  ) : (
                    <Icons.FontAwesomeIcon
                      icon={
                        props.isPlaying
                          ? Icons.Solid.faStop
                          : Icons.Solid.faPlay
                      }
                      size="sm"
                      color={props.isSelected ? 'black' : 'white'}
                      style={{ marginLeft: props.isPlaying ? 0 : 1 }}
                    />
                  )}
                </UI.Pressable>
              </UI.Box>
            )}
          </UI.Row>
        </Semantics.Button>
      </UI.Box>
    )
  })

export const ScreenConnectedAccountGong = buildComponent()
  .withLocale('screens.connected.account-gong.screen')
  .withLifecycle(() => {
    const Store = useStore()
    const navigation = useNavigation()
    const availableGongs = useStoreState((store) => store.audio.gongs)
    const userGong = useStoreState((store) => store.profile.user?.gong)
    const locale = useStoreState((store) => store.utils.device.locale)
    const [selectedGongId, setSelectedGongId] = useState<string | null>(
      userGong?._id || null,
    )
    const [isLoading, setIsLoading] = useState(false)
    const [pendingGongId, setPendingGongId] = useState<string | null>(null)
    const pendingGongIdRef = useRef<string | null>(null)

    const {
      playingGongId,
      isLoading: isLoadingAudio,
      playGong,
      stopGong,
      playerProps,
    } = useGongPreview()

    useAppearance({
      action: 'back',
    })

    // Synchronize selectedGongId with userGong when it changes
    useEffect(() => {
      setSelectedGongId(userGong?._id || null)
    }, [userGong?._id])

    // Stop audio when leaving the page
    useEffect(() => {
      return () => {
        pendingGongIdRef.current = null
        stopGong()
      }
    }, [stopGong])

    const handleSaveSelection = async () => {
      setIsLoading(true)
      try {
        await graphql.mutations.setGong(selectedGongId)
        // Update user data to reflect the change
        Store.getActions().profile.updateUser({
          gong: selectedGongId
            ? availableGongs.find((gong: Gong) => gong._id === selectedGongId) || null
            : null,
        })
        // Navigate back
        navigation.goBack()
      } catch (error) {
        console.error('Error saving gong selection:', error)
      } finally {
        setIsLoading(false)
      }
    }

    const handlePlayGong = async (gong: Gong) => {
      if (playingGongId === gong._id) {
        stopGong()
        return
      }

      pendingGongIdRef.current = gong._id
      setPendingGongId(gong._id)

      try {
        const audioUrl = await getGongDownloadUrl(gong.slug)
        if (pendingGongIdRef.current !== gong._id) {
          return
        }
        playGong(gong._id, audioUrl)
      } catch (error) {
        console.error('Error getting gong audio URL:', error)
      } finally {
        if (pendingGongIdRef.current === gong._id) {
          pendingGongIdRef.current = null
          setPendingGongId(null)
        }
      }
    }

    const hasChanged = selectedGongId !== userGong?._id

    return {
      availableGongs,
      selectedGongId,
      setSelectedGongId,
      handleSaveSelection,
      handlePlayGong,
      isLoading,
      hasChanged,
      locale,
      playingGongId,
      pendingGongId,
      isLoadingAudio,
      stopGong,
      playerProps,
    }
  })
  .withRender(({ lifecycle, locale }) => (
    <UI.ScrollView
      scrollIndicatorInsets={{ right: 1 }}
      mx={[0, 'auto']}
      width="100%"
      maxWidth={['100%', 600]}
    >
      <UI.View mx={[0, 'auto']} width="100%" maxWidth={['100%', 600]}>
        <UI.Text
          style={fonts.bold}
          marginY={4}
          marginX={4}
          color="white"
          textAlign="center"
          fontSize="md"
        >
          {locale('description')}
        </UI.Text>

        <UI.VStack space={2} paddingBottom={4}>
          {/* Option: No gong */}
          <GongItem
            gong={null}
            isSelected={lifecycle.selectedGongId === null}
            onSelect={() => lifecycle.setSelectedGongId(null)}
            locale={lifecycle.locale}
          />

          {/* Available gongs */}
          {lifecycle.availableGongs.map((gong: Gong) => (
            <GongItem
              key={gong._id}
              gong={gong}
              isSelected={lifecycle.selectedGongId === gong._id}
              onSelect={() => lifecycle.setSelectedGongId(gong._id)}
              onPlay={() => lifecycle.handlePlayGong(gong)}
              isPlaying={lifecycle.playingGongId === gong._id}
              isLoading={
                lifecycle.pendingGongId === gong._id ||
                (lifecycle.isLoadingAudio &&
                  lifecycle.playingGongId === gong._id)
              }
              locale={lifecycle.locale}
            />
          ))}
        </UI.VStack>

        {/* Save button */}
        {lifecycle.hasChanged && (
          <UI.Box paddingX={2} paddingY={4}>
            <Semantics.Button
              onPress={lifecycle.handleSaveSelection}
              isLoading={lifecycle.isLoading}
              isLoadingText={locale('saving')}
              leftIcon={
                <Icons.FontAwesomeIcon
                  color="white"
                  icon={Icons.Solid.faCheck}
                />
              }
            >
              {locale('save')}
            </Semantics.Button>
          </UI.Box>
        )}
      </UI.View>

      {/* Hidden Player component for gong preview */}
      {lifecycle.playerProps.source && (
        <Player
          ref={lifecycle.playerProps.setPlayerRef}
          source={lifecycle.playerProps.source}
          audioOnly
          paused={lifecycle.playerProps.paused}
          volume={0.7}
          style={{ display: 'none' }}
          onLoad={lifecycle.playerProps.onLoad}
          onLoadStart={lifecycle.playerProps.onLoadStart}
          onProgress={lifecycle.playerProps.onProgress}
          onEnd={lifecycle.playerProps.onEnd}
          onError={lifecycle.playerProps.onError}
          playInBackground={false}
          ignoreSilentSwitch="ignore"
        />
      )}
    </UI.ScrollView>
  ))
